import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChatMessage, AgentChatUser } from "@/components/agent/chat/types";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/types/database.types";
import { useDataFetching } from "./useDataFetching"; // To fetch rules/knowledge

const EDGE_FUNCTION_NAME = "chat-with-agent";

interface SendMessageVariables {
  agent: Tables<"agents">;
  organization: Tables<"organizations">;
  isPublic: boolean;
  chatMessages: ChatMessage[]; // Current messages for context
  newMessageText: string;
  conversationId: string; // Must have a conversation ID before sending
  previousResponseId: string | null;
  agentChatUser: AgentChatUser | null;
}

interface SendMessageResponse {
  response: string;
  conversation_id: string;
  conversationState?: string;
  responseId?: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  calendly?: {
    tool_call_id: string;
    response_id: string;
    button_text: string;
  } | null;
}

// Helper function to prepare the agentChatUser data
function prepareAgentChatUserData(
  agentChatUser: AgentChatUser | null,
  agentChatUserData: any | null,
): AgentChatUser | null {
  if (!agentChatUser) return null;

  // Make a deep copy to avoid mutating the original
  const preparedUser = JSON.parse(JSON.stringify(agentChatUser));

  // Add any missing phone number from agentChatUserData if available
  if (agentChatUserData?.phone && !preparedUser.phone) {
    preparedUser.phone = agentChatUserData.phone;
  }

  // For debugging
  console.log(
    "[SEND MESSAGE] Original agentChatUser:",
    JSON.stringify(agentChatUser, null, 2),
  );
  console.log(
    "[SEND MESSAGE] Prepared agentChatUser:",
    JSON.stringify(preparedUser, null, 2),
  );
  console.log(
    "[SEND MESSAGE] pre_session_fields type:",
    typeof preparedUser.pre_session_fields,
  );
  console.log(
    "[SEND MESSAGE] pre_session_fields keys:",
    preparedUser.pre_session_fields
      ? Object.keys(preparedUser.pre_session_fields).length > 0
        ? Object.keys(preparedUser.pre_session_fields)
        : "empty object"
      : "undefined",
  );

  return preparedUser;
}

// Move this outside the function
export function useSendMessage(): UseMutationResult<
  SendMessageResponse,
  Error,
  SendMessageVariables,
  { previousMessages: [] }
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { fetchOrganizationData } = useDataFetching(); // Move this here

  const sendMessageToEdgeFunction = async (
    variables: SendMessageVariables,
  ): Promise<SendMessageResponse> => {
    const {
      agent,
      organization,
      isPublic,
      chatMessages,
      newMessageText,
      conversationId,
      previousResponseId,
      agentChatUser,
    } = variables;

    console.log(
      `[RQ Mutate] Sending message for conversation: ${conversationId}`,
    );

    const userMessage: ChatMessage = {
      type: "user",
      content: newMessageText,
      timestamp: new Date().toISOString(),
    };

    // Use the function we got from the hook
    const { behaviors, knowledgeItems } = await fetchOrganizationData(
      organization?.id,
      agent?.id, // Pass the agent ID as well
    );

    // Embed organization data in agent object for edge function (only what's needed for context)
    const contactInfo = organization?.contact_info as {
      website?: string;
      phone?: string;
      email?: string;
    } | null;
    const agentWithOrg = {
      ...agent,
      organization_id: organization?.id,
      organization: organization
        ? {
            name: organization.name,
            industry: undefined, // TODO: Need to join industry table if needed
            website: organization.website || contactInfo?.website,
            phone: contactInfo?.phone,
            email: contactInfo?.email,
            business_overview: organization.business_overview,
            services_overview: organization.services_overview,
          }
        : undefined,
    };

    // If we have agentChatUser, debug log its properties
    if (agentChatUser) {
      console.log("[RQ Mutate] agentChatUser details:", {
        id: agentChatUser.id,
        name: agentChatUser.name,
        email: agentChatUser.email,
        phone: agentChatUser.phone || "Not set",
        has_pre_session_fields: agentChatUser.pre_session_fields ? "Yes" : "No",
        pre_session_fields_type: agentChatUser.pre_session_fields
          ? typeof agentChatUser.pre_session_fields
          : "N/A",
        pre_session_fields_keys: agentChatUser.pre_session_fields
          ? Object.keys(agentChatUser.pre_session_fields)
          : [],
        pre_session_fields: JSON.stringify(
          agentChatUser.pre_session_fields || {},
          null,
          2,
        ),
      });
    }

    // Prepare payload for the edge function
    const payload = {
      agent: agentWithOrg,
      messages: [...chatMessages, userMessage], // Include the new user message
      conversationId: conversationId,
      isPublic: isPublic,
      behaviors: behaviors || [],
      agentChatUser: prepareAgentChatUserData(agentChatUser, null),
      previousResponseId: previousResponseId?.startsWith("resp_")
        ? previousResponseId
        : null,
    };

    console.log(`[RQ Mutate] Calling edge function '${EDGE_FUNCTION_NAME}'`);
    console.log(`[RQ Mutate] Payload (truncated):`, {
      agent_id: payload.agent.id,
      conversation_id: payload.conversationId,
      message_count: payload.messages.length,
      has_agentChatUser: !!payload.agentChatUser,
      agentChatUser: payload.agentChatUser
        ? {
            id: payload.agentChatUser.id,
            phone: payload.agentChatUser.phone || "Not set",
            has_pre_session_fields: !!payload.agentChatUser.pre_session_fields,
          }
        : null,
    });

    const { data, error } =
      await supabase.functions.invoke<SendMessageResponse>(EDGE_FUNCTION_NAME, {
        body: payload,
      });

    if (error) {
      console.error("[RQ Mutate] Edge function error:", error);
      throw new Error(error.message || "Failed to get response from assistant");
    }

    console.log(`[RQ Mutate] Received response from edge function:`, {
      status: "success",
      response_length: data?.response.length || 0,
      has_calendly: !!data?.calendly,
    });

    return data;
  };

  return useMutation({
    mutationFn: sendMessageToEdgeFunction,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["conversations", variables.conversationId],
      });

      console.log(`[RQ Mutate onMutate] Starting mutation...`);
      return { previousMessages: [] };
    },
    onError: (error, variables, context) => {
      console.error("[RQ Mutate onError] Error in mutation:", error);
      // Show toast with error
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "Failed to get a response from the assistant. Please try again.",
      });
    },
    onSuccess: (data, variables, context) => {
      console.log(`[RQ Mutate onSuccess] Mutation successful`);
      // No need to manually update the cache here
      // The component will handle adding the message to the state
    },
    onSettled: (data, error, variables, context) => {
      // Invalidate the conversation cache to ensure it's up to date
      queryClient.invalidateQueries({
        queryKey: ["conversations", variables.conversationId],
      });
    },
  });
}
